<template>
  <v-dialog
    :model-value="dialog"
    @update:model-value="$emit('update:dialog', $event)"
    width="auto"
  >
    <v-card :width="cardWidth">
      <v-toolbar color="primary">
        <v-toolbar-title>
          <span class="text-subtitle-1 text-white">
            {{ ahuDevice.controlFriendlyName }}
            {{ ahuDevice.siteLocationName }}
          </span>
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <!--Dirty Icon-->
        <v-tooltip location="bottom" content-class="vav-control-tooltip">
          <template v-slot:activator="{ props }">
            <v-icon
              v-if="isAHUDirty"
              class="mr-4 ahu-control-dirty-icon"
              color="accentOrange"
              v-bind="props"
            >
              {{ 'mdi-alert-circle' }}
            </v-icon>
          </template>
          <!--Warnings-->
          <AHUDialogAlert
            :isAHUDirty="isAHUDirty"
            :getAHUNewSettings="getAHUNewSettings"
          />
        </v-tooltip>
      </v-toolbar>

      <v-container fluid class="pa-4">
        <!-- Same spacing model as the schema-driven Controls AHU/VAV cards;
             see Styles/_schema-card-grid.scss. -->
        <div class="dt-card-grid">
          <!--Pressure Setpoints Card-->
          <div class="dt-card-cell dt-card-cell--group dt-card-cell--third">
            <v-card variant="outlined">
              <v-card-title
                class="text-primary text-caption text-sm-subtitle-1 font-weight-bold"
              >
                Pressure Setpoints
              </v-card-title>
              <v-divider class="mb-2" />
              <v-card-text class="pa-2">
                <div class="dt-card-grid dt-card-grid--nested">
                  <div class="dt-card-cell dt-card-cell--half">
                    <v-text-field
                      class="rounded-2 text-primary mb-n2"
                      label="Building Static Pressure Setpoint"
                      :model-value="ahuDevice.buildingStaticPressureSetpoint"
                      variant="outlined"
                      density="compact"
                      readonly
                    />
                  </div>
                  <div class="dt-card-cell dt-card-cell--half">
                    <v-text-field
                      class="rounded-2 text-primary mb-n2"
                      label="Building Static Pressure Setpoint (Sensor)"
                      :model-value="
                        ahuDevice.buildingStaticPressureSetpointActualValue
                      "
                      variant="outlined"
                      density="compact"
                      readonly
                    />
                  </div>
                  <div class="dt-card-cell dt-card-cell--half">
                    <v-number-input
                      class="rounded-2 text-primary mb-n2"
                      :color="userCanUpdate ? 'warning' : 'primary'"
                      :readonly="!userCanUpdate"
                      :base-color="userCanUpdate ? 'warning' : 'primary'"
                      inset
                      data-testid="ductStaticPressureSetpointTextField"
                      control-variant="stacked"
                      :step="0.1"
                      :precision="2"
                      label="Duct Static Pressure Setpoint"
                      v-model="localSettings.ductStaticPressureSetpoint"
                      variant="outlined"
                      density="compact"
                    >
                      <template #append-inner v-if="userCanUpdate">
                        <v-icon color="warning">mdi-pencil-box</v-icon>
                      </template>
                    </v-number-input>
                  </div>
                  <div class="dt-card-cell dt-card-cell--half">
                    <v-text-field
                      class="rounded-2 text-primary mb-n2"
                      label="Duct Static Pressure Setpoint (Sensor)"
                      :model-value="
                        ahuDevice.ductStaticPressureSetpointActualValue
                      "
                      variant="outlined"
                      density="compact"
                      readonly
                    />
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
          <!--Building Occupancy Card-->
          <div class="dt-card-cell dt-card-cell--group dt-card-cell--third">
            <v-card variant="outlined">
              <v-card-title
                class="text-primary text-caption text-sm-subtitle-1 font-weight-bold"
              >
                Building Occupancy
              </v-card-title>
              <v-divider class="mb-2" />
              <v-card-text class="pa-2">
                <div class="dt-card-grid dt-card-grid--nested">
                  <div class="dt-card-cell">
                    <v-text-field
                      class="rounded-2 text-primary mb-n2"
                      label="AHU Mode [ Occupied | Unoccupied | Tenant Override ]"
                      :model-value="ahuDevice.occupationMode"
                      variant="outlined"
                      density="compact"
                      readonly
                    />
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
          <!--Supply Temperature Setpoints Card-->
          <div class="dt-card-cell dt-card-cell--group dt-card-cell--third">
            <v-card variant="outlined">
              <v-card-title
                class="text-primary text-caption text-sm-subtitle-1 font-weight-bold"
              >
                Supply Temperature Setpoints
              </v-card-title>
              <v-divider class="mb-2" />
              <v-card-text class="pa-2">
                <div class="dt-card-grid dt-card-grid--nested">
                  <div class="dt-card-cell dt-card-cell--half">
                    <v-number-input
                      class="rounded-2 text-primary mb-n2"
                      :color="userCanUpdate ? 'warning' : 'primary'"
                      :readonly="!userCanUpdate"
                      :base-color="userCanUpdate ? 'warning' : 'primary'"
                      data-testid="netSupplyAirCoolingSetpointTextField"
                      control-variant="stacked"
                      inset
                      :step="1"
                      :precision="2"
                      label="Net Supply Air Cooling Setpoint"
                      v-model="localSettings.netSupplyAirCoolingSetpoint"
                      variant="outlined"
                      density="compact"
                    >
                      <template #append-inner v-if="userCanUpdate">
                        <v-icon color="warning">mdi-pencil-box</v-icon>
                      </template>
                    </v-number-input>
                  </div>
                  <div class="dt-card-cell dt-card-cell--half">
                    <v-number-input
                      class="rounded-2 text-primary mb-n2"
                      :color="userCanUpdate ? 'warning' : 'primary'"
                      :readonly="!userCanUpdate"
                      :base-color="userCanUpdate ? 'warning' : 'primary'"
                      data-testid="minSupplyAirCoolingSetpointTextField"
                      control-variant="stacked"
                      inset
                      :step="1"
                      :precision="2"
                      label="Min Supply Air Cooling Setpoint"
                      v-model="localSettings.minSupplyAirCoolingSetpoint"
                      variant="outlined"
                      density="compact"
                    >
                      <template #append-inner v-if="userCanUpdate">
                        <v-icon color="warning">mdi-pencil-box</v-icon>
                      </template>
                    </v-number-input>
                  </div>
                  <div class="dt-card-cell dt-card-cell--half">
                    <v-text-field
                      class="rounded-2 text-primary mb-n2"
                      label="Effective Supply Air Cooling Setpoint"
                      :model-value="ahuDevice.effectiveSupplyAirCoolingSetpoint"
                      variant="outlined"
                      density="compact"
                      readonly
                    />
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
          <!--System Parameters Card-->
          <div class="dt-card-cell dt-card-cell--group">
            <v-card variant="outlined">
              <v-card-title
                class="text-primary text-caption text-sm-subtitle-1 font-weight-bold"
              >
                System Parameters
              </v-card-title>
              <v-divider class="mb-2" />
              <v-card-text class="pa-2">
                <div class="dt-card-grid dt-card-grid--nested">
                  <div class="dt-card-cell dt-card-cell--third">
                    <v-text-field
                      class="rounded-2 text-primary mb-n2"
                      label="Airflow Status [ Flow | No Flow ]"
                      :model-value="ahuDevice.airFlowStatus"
                      variant="outlined"
                      density="compact"
                      readonly
                    />
                  </div>
                  <div class="dt-card-cell dt-card-cell--third">
                    <v-text-field
                      class="rounded-2 text-primary mb-n2"
                      label="Supply Fan Capacity %"
                      :model-value="ahuDevice.supplyFanCapacity"
                      variant="outlined"
                      density="compact"
                      readonly
                    />
                  </div>
                  <div class="dt-card-cell dt-card-cell--third">
                    <v-text-field
                      class="rounded-2 text-primary mb-n2"
                      label="Return Fan Capacity %"
                      :model-value="ahuDevice.returnFanCapacity"
                      variant="outlined"
                      density="compact"
                      readonly
                    />
                  </div>
                  <div class="dt-card-cell dt-card-cell--third">
                    <v-text-field
                      class="rounded-2 text-primary mb-n2"
                      label="Return Air Temperature (RAT)"
                      :model-value="ahuDevice.returnAirTemperature"
                      variant="outlined"
                      density="compact"
                      readonly
                    />
                  </div>
                  <div class="dt-card-cell dt-card-cell--third">
                    <v-text-field
                      class="rounded-2 text-primary mb-n2"
                      label="Supply Air Temperature (SAT)"
                      :model-value="ahuDevice.supplyAirTemperature"
                      variant="outlined"
                      density="compact"
                      readonly
                    />
                  </div>
                  <div class="dt-card-cell dt-card-cell--third">
                    <v-text-field
                      class="rounded-2 text-primary mb-n2"
                      label="Outside Air Temperature (OAT)"
                      :model-value="ahuDevice.outsideAirTemperature"
                      variant="outlined"
                      density="compact"
                      readonly
                    />
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
          <!--Warning, Problem, Fault-->
          <div class="dt-card-cell dt-card-cell--group">
            <v-card variant="outlined">
              <v-card-text class="pa-2">
                <div class="dt-card-grid dt-card-grid--nested">
                  <div
                    v-for="status in statusFlags"
                    :key="status.label"
                    class="dt-card-cell dt-card-cell--third dt-card-cell--status"
                  >
                    <v-card
                      variant="outlined"
                      color="primary"
                      width="120px"
                      class="pa-2"
                    >
                      <v-icon
                        :color="ahuDevice[status.key] ? 'red' : 'green'"
                        :icon="
                          ahuDevice[status.key]
                            ? 'mdi-alert-circle'
                            : 'mdi-check-circle'
                        "
                        start
                        size="large"
                      />
                      <span class="text-primary text-caption text-sm-subtitle-1">
                        {{ status.label }}
                      </span>
                    </v-card>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </div>
        <v-row class="mt-2">
          <v-col cols="8" align-self="center"> </v-col>
          <v-col cols="2">
            <v-btn
              :disabled="isAHUSetLoading"
              data-testid="resetAHUSetpointButton"
              class="rounded-2"
              color="primary"
              variant="flat"
              block
              size="large"
              @click="isAHUDirty ? resetChangedSettings() : handleCancel()"
            >
              <div class="text-subtitle-2 text-white text-truncate">
                {{ isAHUDirty ? 'Reset' : 'Cancel' }}
              </div>
            </v-btn>
          </v-col>
          <v-col cols="2">
            <v-btn
              :loading="isAHUSetLoading"
              :disabled="!isAHUDirty || !userCanUpdate"
              class="rounded-2"
              :color="!isAHUDirty ? 'grey' : 'accentOrange'"
              data-testid="setAHUSetpointButton"
              variant="flat"
              block
              size="large"
              @click="handleSet"
            >
              <div class="text-subtitle-2 text-white text-truncate">Set</div>
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed, inject } from 'vue';
import { useDisplay } from 'vuetify';

import API from '@/Core.Service.Domain/Controls/DigitalTwin/MGMOB/API';

import { IAHU } from '@/Core.Service.Domain/Controls/Controls.Common/types/index.ts';

import AHUDialogAlert from '@/Core.Service.Domain/Controls/DigitalTwin/MGMOB/Components/AHUDialogAlert.vue';

import { capitalizeFirstLetter } from '@/Core.Patterns/Adapter/index.ts';
import { splitCamelCase } from '@/Core.Patterns/Factory/index.ts';

import { ToastService } from '@/plugins/toasts';

import { useAbility } from '@/plugins/useAbility.ts';

const { name } = useDisplay();

const isAHUSetLoading = ref(false);
const isDirty = ref({
  netSupplyAirCoolingSetpoint: false,
  minSupplyAirCoolingSetpoint: false,
  ductStaticPressureSetpoint: false,
});

const isAHUDirty = computed(() =>
  Object.values(isDirty.value).some((value) => value)
);

const cardWidth = computed(() => {
  switch (name.value) {
    case 'xs':
      return '95vw';
    case 'sm':
      return '95vw';
    case 'md':
      return '95vw';
    case 'lg':
      return '95vw';
    case 'xl':
      return '85vw';
    case 'xxl':
      return '60vw';
    default:
      return undefined;
  }
});

const props = defineProps({
  ahuDevice: {
    type: Object as () => IAHU,
    required: true,
  },
  dialog: Boolean,
});

const emits = defineEmits(['update:dialog']);

const statusFlags: { key: 'warning' | 'problem' | 'fault'; label: string }[] = [
  { key: 'warning', label: 'Warning' },
  { key: 'problem', label: 'Problem' },
  { key: 'fault', label: 'Fault' },
];

const localSettings = ref<
  Pick<
    IAHU,
    | 'netSupplyAirCoolingSetpoint'
    | 'minSupplyAirCoolingSetpoint'
    | 'ductStaticPressureSetpoint'
  >
>({
  netSupplyAirCoolingSetpoint: 0,
  minSupplyAirCoolingSetpoint: 0,
  ductStaticPressureSetpoint: 0,
});

watch(
  () => [
    props.ahuDevice.netSupplyAirCoolingSetpoint,
    props.ahuDevice.minSupplyAirCoolingSetpoint,
    props.ahuDevice.ductStaticPressureSetpoint,
  ],
  () => {
    localSettings.value.netSupplyAirCoolingSetpoint =
      props.ahuDevice.netSupplyAirCoolingSetpoint;
    localSettings.value.minSupplyAirCoolingSetpoint =
      props.ahuDevice.minSupplyAirCoolingSetpoint;
    localSettings.value.ductStaticPressureSetpoint =
      props.ahuDevice.ductStaticPressureSetpoint;
  },
  { immediate: true }
);

watch(
  () => [
    localSettings.value.netSupplyAirCoolingSetpoint,
    localSettings.value.minSupplyAirCoolingSetpoint,
    localSettings.value.ductStaticPressureSetpoint,
  ],
  ([netVal, minVal, ductVal]) => {
    isDirty.value.netSupplyAirCoolingSetpoint =
      Number(netVal) !== props.ahuDevice.netSupplyAirCoolingSetpoint;
    isDirty.value.minSupplyAirCoolingSetpoint =
      Number(minVal) !== props.ahuDevice.minSupplyAirCoolingSetpoint;
    isDirty.value.ductStaticPressureSetpoint =
      Number(ductVal) !== props.ahuDevice.ductStaticPressureSetpoint;
  }
);

const ability = useAbility();
const userCanUpdate = computed(() => {
  return ability.can('update', 'controls');
});

const toast = inject<ToastService>('toast')!;
const handleSet = async () => {
  if (!isAHUDirty.value || !userCanUpdate.value) return;
  type PermissibleKeys =
    | 'netSupplyAirCoolingSetpoint'
    | 'minSupplyAirCoolingSetpoint'
    | 'ductStaticPressureSetpoint';
  const payload = Object.entries(isDirty.value).reduce(
    (acc, [key, isChanged]) => {
      if (isChanged) {
        acc[key as PermissibleKeys] =
          localSettings.value[key as PermissibleKeys];
      }
      return acc;
    },
    {} as Partial<IAHU>
  );

  isAHUSetLoading.value = true;

  try {
    await API.setAHUSetpoint(props.ahuDevice.deviceId, payload);
    resetIsDirty();

    toast.show({
      title: 'Success',
      message: `AHU Setpoints have been successfully updated.<br />Values can take up to 2 minutes to reflect.`,
      status: 'success',
    });
  } catch (error) {
    return Promise.reject(error);
  } finally {
    isAHUSetLoading.value = false;
  }
};

const resetIsDirty = () => {
  isDirty.value = {
    netSupplyAirCoolingSetpoint: false,
    minSupplyAirCoolingSetpoint: false,
    ductStaticPressureSetpoint: false,
  };
};

const resetChangedSettings = () => {
  if (!isAHUDirty.value) return;
  localSettings.value.netSupplyAirCoolingSetpoint =
    props.ahuDevice.netSupplyAirCoolingSetpoint;
  localSettings.value.minSupplyAirCoolingSetpoint =
    props.ahuDevice.minSupplyAirCoolingSetpoint;
  localSettings.value.ductStaticPressureSetpoint =
    props.ahuDevice.ductStaticPressureSetpoint;

  resetIsDirty();
};

const handleCancel = () => {
  resetChangedSettings();
  emits('update:dialog', false);
};

const getAHUNewSettings = () => {
  const dirtySetting: {
    name: string;
    currentSetting: string;
    newSetting: string;
  }[] = [];

  Object.entries(isDirty.value).forEach(([key, value]) => {
    if (value) {
      const typedKey = key as keyof IAHU;
      dirtySetting.push({
        name: splitCamelCase(capitalizeFirstLetter(`${key}`)),
        currentSetting: `${props.ahuDevice[typedKey]}`,
        newSetting: `${
          localSettings.value[
            typedKey as
              | 'netSupplyAirCoolingSetpoint'
              | 'minSupplyAirCoolingSetpoint'
          ]
        }`,
      });
    }
  });

  return dirtySetting;
};
</script>

<style lang="scss" scoped>
@use '@/Core.Service.Domain/Controls/DigitalTwin/MGMOB/Styles/schema-card-grid' as *;

@include schema-card-grid;

.v-field__append-inner > .v-icon {
  opacity: 1 !important;
}

.ahu-control-dirty-icon {
  cursor: pointer;
}

:deep(input[readonly]) {
  color: grey !important;
  cursor: not-allowed;
}
</style>
